// PersonTable.jsx

import React, { useMemo, useState, useEffect } from 'react';
import ExcelUploader from './ExcelUploader';
import PersonRow from './PersonRow';


function PersonTable({
  people = [],
  earningsData = [],
  userPeriodData = {},
  selectedNationality,
  updateEarnings,
  selectedPeriod,
  selectedYear,
  selectedMonth,
  onDeletePerson,
  onUpdatePerson,
  refreshData,
  firms = [],
  selectedFirm = '',
}) {
  // Default vrijednosti za stupce
  const defaultHiddenColumns = {
    riderID: false,
    grad: false,
    firma: false,
    nationality: false,
    zaradaTotal: false,
    fee: false,
    feeIznos: false,
    total1: false,
    appKnjig: false,
    doprinosi: false,
    extraDoprinosi: false,
    renta: false,
    rentSmjestaj: false,
    tarifa: false,
    struja: false,
    glovoTax: false,
    akontacija: false,
    gorivo: false,
    ostalo: false,
    bonus: false,
    total2: false,
  };

  // Učitaj spremljene postavke iz localStorage pri inicijalizaciji
  const [hiddenColumns, setHiddenColumns] = useState(() => {
    try {
      const saved = localStorage.getItem('personTableColumns');
      if (saved) {
        const parsed = JSON.parse(saved);
        return { ...defaultHiddenColumns, ...parsed };
      }
    } catch (e) {
      console.warn('Greška pri učitavanju postavki stupaca:', e);
    }
    return defaultHiddenColumns;
  });

  // Spremi postavke u localStorage kad se promijene
  useEffect(() => {
    try {
      localStorage.setItem('personTableColumns', JSON.stringify(hiddenColumns));
    } catch (e) {
      console.warn('Greška pri spremanju postavki stupaca:', e);
    }
  }, [hiddenColumns]);

  // Stanje za otvorenost dropdown menija
  const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false);

  // Zatvaranje dropdown menija na ESC tipku
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isColumnMenuOpen) {
        setIsColumnMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isColumnMenuOpen]);

  // Determine the number of weeks and starting week
  let numOfWeeks;
  let startWeek;

  if (selectedNationality === 'Hrvat') {
    if (selectedPeriod === 1) {
      numOfWeeks = 2; // Weeks 1 and 2
      startWeek = 1;
    } else if (selectedPeriod === 2) {
      numOfWeeks = 3; // Weeks 3, 4, and 5
      startWeek = 3;
    }
  } else {
    numOfWeeks = 5;
    startWeek = 1;
  }

  // Filter people based on selected nationality and firm, and exclude archived persons
  const filteredPeople = useMemo(() => {
    return people
      .filter((person) => {
        const nationalityMatch =
          selectedNationality === 'Sve' || person.nationality === selectedNationality;
        const firmMatch = selectedFirm === '' || person.firmId === selectedFirm;
        const dateMatch =
          (person.startYear < selectedYear ||
            (person.startYear === selectedYear && person.startMonth <= selectedMonth)) &&
          (!person.archived ||
            person.archiveYear > selectedYear ||
            (person.archiveYear === selectedYear && person.archiveMonth > selectedMonth));
        const notArchived = !person.archived; // Dodani uvjet za arhivirane osobe
        return nationalityMatch && firmMatch && dateMatch && notArchived;
      })
      .sort((a, b) => {
        if (selectedFirm === '') {
          // Sort by firm name first if no firm is selected, then by person ID
          const firmA = firms.find(firm => firm.id === a.firmId)?.name || '';
          const firmB = firms.find(firm => firm.id === b.firmId)?.name || '';
          if (firmA === firmB) {
            return a.id.localeCompare(b.id);
          }
          return firmA.localeCompare(firmB);
        } else {
          // If a firm is selected, sort only by person ID
          return a.id.localeCompare(b.id);
        }
      });
  }, [people, selectedNationality, selectedFirm, selectedYear, selectedMonth, firms]);

  // Function to calculate sums
  const sums = useMemo(() => {
    const sums = {
      // Earnings per week
      ...Array.from({ length: numOfWeeks }, (_, i) => [`tjedan${startWeek + i}`, 0]).reduce(
        (acc, [key, value]) => {
          acc[key] = value;
          return acc;
        },
        {}
      ),
      // Wolt zarade
      w1: 0,
      w2: 0,
      // Other sums
      zaradaTotal: 0,
      feeIznos: 0,
      total1: 0,
      appKnjig: 0,
      doprinosi: 0,
      extraDoprinosi: 0,
      renta: 0,
      rentSmjestaj: 0,
      oprema: 0,
      akontacija: 0,
      gorivo: 0,
      ostalo: 0,
      bonus: 0,
      total2: 0,
      tarifa: 0, // Added
      depozit: 0, // Added
      struja: 0, // Added
      glovoTax: 0, // Added
    };

    filteredPeople.forEach((person) => {
      // Filter earnings for this person - filtrirati po godini i mjesecu
      let personEarnings = earningsData.filter((earning) => 
        earning.userId === person.id &&
        earning.year === selectedYear &&
        earning.month === selectedMonth
      );

      // Funkcija za dohvaćanje vrijednosti iz periodData ili person objekta
      const getValue = (fieldName) => {
        const periodValue = userPeriodData[person.id]?.[fieldName];
        if (periodValue !== undefined) return parseFloat(periodValue) || 0;
        const personValue = person[fieldName];
        return parseFloat(personValue) || 0;
      };

      // Sum earnings per week (Glovo) i Wolt zarade
      personEarnings.forEach((earning) => {
        // Provjera je li to Wolt zarada
        const isWolt = earning.platform === 'wolt' || earning.week === 'w1' || earning.week === 'w2';
        
        if (isWolt) {
          // Wolt zarade - W1 ili W2
          if (earning.week === 'w1') {
            sums.w1 += parseFloat(earning.amount) || 0;
          } else if (earning.week === 'w2') {
            sums.w2 += parseFloat(earning.amount) || 0;
          }
        } else {
          // Glovo zarade - normalni tjedni
          const weekNumber = earning.week;
          const weekKey = `tjedan${weekNumber}`;
          if (sums.hasOwnProperty(weekKey)) {
            sums[weekKey] += parseFloat(earning.amount) || 0;
          }
        }
      });

      // Calculate zaradaTotal for this person (uključuje i Glovo i Wolt)
      const zaradaTotal = personEarnings.reduce(
        (sum, earning) => sum + (parseFloat(earning.amount) || 0),
        0
      );

      // Get all financial values using getValue function
      const fee = getValue('fee');
      const feeIznos = (zaradaTotal * fee) / 100;
      const total1 = zaradaTotal - feeIznos;

      // Get all deductions
      const appKnjig = getValue('appKnjig');
      const doprinosi = getValue('doprinosi');
      const extraDoprinosi = getValue('extraDoprinosi');
      const renta = getValue('renta');
      const rentSmjestaj = getValue('rentSmjestaj');
      const oprema = getValue('oprema');
      const akontacija = getValue('akontacija');
      const gorivo = getValue('gorivo');
      const ostalo = getValue('ostalo');
      const bonus = getValue('bonus');

      // Get additional deductions for foreigners
      const tarifa = getValue('tarifa');
      const depozit = getValue('depozit');
      const struja = getValue('struja');
      const glovoTax = getValue('glovoTax');

      // Calculate total2
      const total2 =
        total1 -
        appKnjig -
        doprinosi -
        extraDoprinosi -
        renta -
        rentSmjestaj -
        oprema -
        akontacija -
        gorivo -
        ostalo -
        (person.nationality === 'Stranac'
          ? tarifa + depozit + struja + glovoTax
          : 0) +
        bonus;

      // Ensure total2 is not negative
      const finalTotal2 = Math.max(0, total2);

      // Sum other fields
      sums.zaradaTotal += zaradaTotal;
      sums.feeIznos += feeIznos;
      sums.total1 += total1;
      sums.appKnjig += appKnjig;
      sums.doprinosi += doprinosi;
      sums.extraDoprinosi += extraDoprinosi;
      sums.renta += renta;
      sums.rentSmjestaj += rentSmjestaj;
      sums.oprema += oprema;
      sums.akontacija += akontacija;
      sums.gorivo += gorivo;
      sums.ostalo += ostalo;
      sums.bonus += bonus;
      sums.total2 += finalTotal2;
      sums.tarifa += tarifa;
      sums.depozit += depozit;
      sums.struja += struja;
      sums.glovoTax += glovoTax;
    });

    return sums;
  }, [filteredPeople, earningsData, userPeriodData, numOfWeeks, startWeek, selectedYear, selectedMonth]);

  // Funkcija za toggle vidljivosti stupca
  const toggleColumn = (columnName) => {
    setHiddenColumns(prev => ({
      ...prev,
      [columnName]: !prev[columnName]
    }));
  };

  // Definicija svih stupaca s prijateljskim imenima
  const allColumns = [
    { key: 'riderID', label: 'Rider ID' },
    { key: 'grad', label: 'Grad' },
    { key: 'firma', label: 'Firma' },
    { key: 'nationality', label: 'N (Nacionalnost)' },
    { key: 'zaradaTotal', label: 'Zarada Total' },
    { key: 'fee', label: 'Fee %' },
    { key: 'feeIznos', label: 'FEE Iznos' },
    { key: 'total1', label: 'Total 1' },
    { key: 'appKnjig', label: 'App Knjig.' },
    { key: 'doprinosi', label: 'Doprinosi' },
    { key: 'extraDoprinosi', label: 'Extra doprinosi' },
    { key: 'renta', label: 'Renta' },
    { key: 'rentSmjestaj', label: 'Rent smještaj' },
    { key: 'tarifa', label: 'Tarifa' },
    { key: 'struja', label: 'Struja' },
    { key: 'glovoTax', label: 'O.F.' },
    { key: 'akontacija', label: 'Akontacija' },
    { key: 'gorivo', label: 'Gorivo' },
    { key: 'ostalo', label: 'Ostalo' },
    { key: 'bonus', label: 'Bonus' },
    { key: 'total2', label: 'Total 2' },
  ];

  return (
    <div className="person-table-container">
      {/* Prikaz broja osoba i kontrole za sakrivanje stupaca */}
      <div className="table-header">
        <span className="people-count">
          Prikazano osoba: {filteredPeople.length}
        </span>
        <div className="column-toggle-wrapper">
          <button 
            className="column-toggle-button"
            onClick={() => setIsColumnMenuOpen(!isColumnMenuOpen)}
          >
            <span>Prikaži stupce</span>
            <span className={`dropdown-arrow ${isColumnMenuOpen ? 'open' : ''}`}>▼</span>
          </button>
          {isColumnMenuOpen && (
            <>
              <div 
                className="dropdown-overlay"
                onClick={() => setIsColumnMenuOpen(false)}
              />
              <div className="column-dropdown-menu">
                <div className="dropdown-header">
                  <h3>Odaberi stupce za prikaz</h3>
                  <button 
                    className="close-dropdown"
                    onClick={() => setIsColumnMenuOpen(false)}
                  >
                    ×
                  </button>
                </div>
                <div className="dropdown-content">
                  {allColumns.map((column) => (
                    <label key={column.key} className="dropdown-item">
                      <input
                        type="checkbox"
                        checked={!hiddenColumns[column.key]}
                        onChange={() => toggleColumn(column.key)}
                      />
                      <span>{column.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <table className="person-table">
        <thead>
          <tr>
            <th>Ime</th>
            <th>Uređivanje</th>
            <th className={hiddenColumns.riderID ? 'hidden-column' : ''}>Rider ID</th>
            <th className={hiddenColumns.grad ? 'hidden-column' : ''}>Grad</th>
            <th className={hiddenColumns.firma ? 'hidden-column' : ''}>Firma</th>
            <th className={hiddenColumns.nationality ? 'hidden-column' : ''}>N</th>
            {[...Array(numOfWeeks)].map((_, i) => (
              <th key={i}>
                <ExcelUploader
                  onDataParsed={(data, filename) => {
                    const weekNumber = startWeek + i;
                    console.log(
                      `Uploading data for week ${weekNumber} with filename ${filename}`
                    );
                    updateEarnings(weekNumber, data, filename);
                  }}
                >
                  Tjedan {startWeek + i}
                </ExcelUploader>
              </th>
            ))}
            <th>W1</th>
            <th>W2</th>
            <th className={hiddenColumns.zaradaTotal ? 'hidden-column' : ''}>Zarada Total</th>
            <th className={`${hiddenColumns.fee ? 'hidden-column' : ''} highlighted-header`}>Fee %</th>
            <th className={`${hiddenColumns.feeIznos ? 'hidden-column' : ''} highlighted-header`}>FEE Iznos</th>
            <th className={`${hiddenColumns.total1 ? 'hidden-column' : ''} highlighted-header`}>Total 1</th>
            <th className={hiddenColumns.appKnjig ? 'hidden-column' : ''}>App Knjig.</th>
            <th className={hiddenColumns.doprinosi ? 'hidden-column' : ''}>Doprinosi</th>
            <th className={hiddenColumns.extraDoprinosi ? 'hidden-column' : ''}>Extra doprinosi</th>
            <th className={hiddenColumns.renta ? 'hidden-column' : ''}>Renta</th>
            <th className={hiddenColumns.rentSmjestaj ? 'hidden-column' : ''}>Rent smještaj</th>
            <th className={hiddenColumns.tarifa ? 'hidden-column' : ''}>Tarifa</th>
            <th className={hiddenColumns.struja ? 'hidden-column' : ''}>Struja</th>
            <th className={hiddenColumns.glovoTax ? 'hidden-column' : ''}>O.F.</th>
            <th className={hiddenColumns.akontacija ? 'hidden-column' : ''}>Akontacija</th>
            <th className={hiddenColumns.gorivo ? 'hidden-column' : ''}>Gorivo</th>
            <th className={hiddenColumns.ostalo ? 'hidden-column' : ''}>Ostalo</th>
            <th className={hiddenColumns.bonus ? 'hidden-column' : ''}>Bonus</th>
            <th className={`${hiddenColumns.total2 ? 'hidden-column' : ''} highlighted-header`}>Total 2</th>
          </tr>

          {/* Summary row appears here */}
          {selectedNationality === 'Sve' && (
            <tr className="summary-row">
              <td>
                <strong>Suma</strong>
              </td>
              <td></td>
              <td className={hiddenColumns.riderID ? 'hidden-column' : ''}></td>
              <td className={hiddenColumns.grad ? 'hidden-column' : ''}></td>
              <td className={hiddenColumns.firma ? 'hidden-column' : ''}></td>
              <td className={hiddenColumns.nationality ? 'hidden-column' : ''}></td>
              {[...Array(numOfWeeks)].map((_, i) => {
                const weekKey = `tjedan${startWeek + i}`;
                return (
                  <td key={i}>
                    <strong>{sums[weekKey].toFixed(2)}</strong>
                  </td>
                );
              })}
              <td>
                <strong>{sums.w1 ? sums.w1.toFixed(2) : '0.00'}</strong>
              </td>
              <td>
                <strong>{sums.w2 ? sums.w2.toFixed(2) : '0.00'}</strong>
              </td>
              <td className={hiddenColumns.zaradaTotal ? 'hidden-column' : ''}>
                <strong>{sums.zaradaTotal.toFixed(2)}</strong>
              </td>
              <td className={hiddenColumns.fee ? 'hidden-column' : ''}></td>
              <td className={hiddenColumns.feeIznos ? 'hidden-column' : ''}>
                <strong>{sums.feeIznos.toFixed(2)}</strong>
              </td>
              <td className={hiddenColumns.total1 ? 'hidden-column' : ''}>
                <strong>{sums.total1.toFixed(2)}</strong>
              </td>
              <td className={hiddenColumns.appKnjig ? 'hidden-column' : ''}>
                <strong>{sums.appKnjig.toFixed(2)}</strong>
              </td>
              <td className={hiddenColumns.doprinosi ? 'hidden-column' : ''}>
                <strong>{sums.doprinosi.toFixed(2)}</strong>
              </td>
              <td className={hiddenColumns.extraDoprinosi ? 'hidden-column' : ''}>
                <strong>{sums.extraDoprinosi.toFixed(2)}</strong>
              </td>
              <td className={hiddenColumns.renta ? 'hidden-column' : ''}>
                <strong>{sums.renta.toFixed(2)}</strong>
              </td>
              <td className={hiddenColumns.rentSmjestaj ? 'hidden-column' : ''}>
                <strong>{sums.rentSmjestaj.toFixed(2)}</strong>
              </td>
              <td className={hiddenColumns.tarifa ? 'hidden-column' : ''}><strong>{sums.tarifa.toFixed(2)}</strong></td> 
              <td className={hiddenColumns.struja ? 'hidden-column' : ''}><strong>{sums.struja.toFixed(2)}</strong></td> 
              <td className={hiddenColumns.glovoTax ? 'hidden-column' : ''}><strong>{sums.glovoTax.toFixed(2)}</strong></td> 
              <td className={hiddenColumns.akontacija ? 'hidden-column' : ''}>
                <strong>{sums.akontacija.toFixed(2)}</strong>
              </td>
              <td className={hiddenColumns.gorivo ? 'hidden-column' : ''}>
                <strong>{sums.gorivo.toFixed(2)}</strong>
              </td>
              <td className={hiddenColumns.ostalo ? 'hidden-column' : ''}>
                <strong>{sums.ostalo.toFixed(2)}</strong>
              </td>
              <td className={hiddenColumns.bonus ? 'hidden-column' : ''}>
                <strong>{sums.bonus.toFixed(2)}</strong>
              </td>
              <td className={hiddenColumns.total2 ? 'hidden-column' : ''}>
                <strong>{sums.total2.toFixed(2)}</strong>
              </td>
            </tr>
          )}
        </thead>
        <tbody>
          {filteredPeople.length > 0 ? (
            filteredPeople.map((person) => (
              <PersonRow
                key={person.id}
                person={person}
                earningsData={earningsData}
                numOfWeeks={numOfWeeks}
                startWeek={startWeek}
                selectedYear={selectedYear}
                selectedMonth={selectedMonth}
                selectedPeriod={selectedPeriod}
                userPeriodData={userPeriodData[person.id] || {}}
                onDeletePerson={onDeletePerson}
                onUpdatePerson={onUpdatePerson}
                refreshData={refreshData}
                firms={firms}
                selectedNationality={selectedNationality}
                hiddenColumns={hiddenColumns}
              />
            ))
            ) : (
              <tr>
                <td colSpan={numOfWeeks + 25} style={{ textAlign: 'center' }}>
                  Nema prikazanih osoba.
                </td>
              </tr>
            )}
        </tbody>
      </table>
    </div>
  );
}

export default PersonTable;
